/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiError, ReqOptions, getApiService } from "./api";
import { PickByValue, ValuesType } from "utility-types";
import { ApiSchema } from "@api-types";
import { Observable, map, of } from "rxjs";
import { ObjectPaths } from "types/object-paths";
type Nullable<T> = { [K in keyof T]: T[K] | null };
type PathTypes = ApiSchema.ApiGetPathTypes &
  ApiSchema.ApiPostPathTypes &
  ApiSchema.ApiPatchPathTypes &
  ApiSchema.ApiDeletePathTypes &
  ApiSchema.ApiGetCountPathTypes &
  ApiSchema.ApiGetOnePathTypes &
  ApiSchema.ApiPostOnePathTypes &
  ApiSchema.ApiPatchOnePathTypes &
  ApiSchema.ApiDeleteOnePathTypes &
  ApiSchema.ApiFuncPathTypes &
  ApiSchema.ApiActPathTypes;
type ErrorPathTypes = ApiSchema.ApiGetErrorTypes &
  ApiSchema.ApiPostErrorTypes &
  ApiSchema.ApiPatchErrorTypes &
  ApiSchema.ApiDeleteErrorTypes &
  ApiSchema.ApiGetCountErrorTypes &
  ApiSchema.ApiGetOneErrorTypes &
  ApiSchema.ApiPostOneErrorTypes &
  ApiSchema.ApiPatchOneErrorTypes &
  ApiSchema.ApiDeleteOneErrorTypes &
  ApiSchema.ApiFuncErrorTypes &
  ApiSchema.ApiActErrorTypes;

type RequestBodyTypes = ApiSchema.ApiPostRequestBody &
  ApiSchema.ApiPatchRequestBody &
  ApiSchema.ApiDeleteRequestBody;

type ParamValueType = string | number | number[] | string[] | boolean | Date | object | null | undefined;

export class OdataBuilder<T, R> {
  private readonly selects: string[] = [];
  private readonly expands: string[] = [];
  private readonly filters: string[] = [];
  private readonly orderBys: string[] = [];
  private readonly params: string[] = [];
  private readonly applys: string[] = [];
  private tops: number | null = null;
  private skips?: number;
  private counts?: boolean;

  constructor(protected level: number = 1) {}

  protected getParamValue = (param?: ParamValueType) => {
    if (param === null || param === undefined) {
      return null;
    } else if (param instanceof Date) {
      return `${encodeURIComponent(param?.toISOString() || "")}`;
    } else if (typeof param === "string" || param instanceof String) {
      const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        param.toString(),
      );
      return isGuid ? param.toString() : `'${encodeURIComponent(param?.toString() || "")}'`;
    } else if (Array.isArray(param) || typeof param === "object") {
      return JSON.stringify(param);
    } else if (param === true || param === false) {
      return param ? "true" : "false";
    }
    return param;
  };
  protected setParams = (url: string, params: { [key in string]: ParamValueType }) => {
    Object.keys(params).forEach((p) => {
      const propString = String(this.getParamValue(params[p]));
      url = url.replace("${" + p + "}", propString);
    });
    url = url.replace(/\$\{(.*?)\}/g, "null");
    return url;
  };

  public select = <TSelects extends keyof T>(...items: TSelects[]) => {
    this.selects.push(...(items as string[]));
    type BuilderReturnType =
      R extends Array<any>
        ? OdataBuilderLevel0<T, Pick<T, TSelects>[]>
        : OdataBuilderLevel0ForOne<T, Pick<T, TSelects>>;
    return this as never as BuilderReturnType;
  };
  public get type(): R {
    return {} as never as R;
  }
  public expand = <
    Z extends keyof PickByValue<T, object | undefined>,
    X = NonNullable<T[Z]> extends Array<any> ? ValuesType<NonNullable<T[Z]>> : NonNullable<T[Z]>,
  >(
    item: Z,
    callback?: (builder: OdataBuilder<X, T[Z]>) => OdataBuilder<X, any>, // todo: omit select
  ) => {
    const v = (callback && callback(new OdataBuilder<X, T[Z]>(this.level + 1)))?.toString();
    this.expands.push(String(item) + (v ? `(${v})` : ""));
    type BuilderReturnType =
      R extends Array<any>
        ? OdataBuilderLevel0<T, (ValuesType<R> & Pick<T, Z>)[]>
        : OdataBuilderLevel0ForOne<T, R & Pick<T, Z>>;
    return this as never as BuilderReturnType;
  };

  public expandAndSelect = <
    TTT extends PickByValue<T, object | undefined>,
    Z extends keyof PickByValue<T, object | undefined>,
    X = NonNullable<TTT[Z]> extends Array<any> ? ValuesType<NonNullable<TTT[Z]>> : NonNullable<TTT[Z]>,
    // Selections directly passed as keys
    TSelects extends keyof X = never,
    // Combine both explicit and callback-based selections
  >(
    item: Z,
    callback?: ((builder: Omit<OdataBuilder<X, TTT[Z]>, "select">) => OdataBuilder<X, any>) | null,
    ...items: TSelects[]
  ) => {
    const builder = new OdataBuilder<X, TTT[Z]>(this.level + 1);
    !callback && builder.select(...items);
    const v = ((callback && callback(builder).select(...items)) ?? builder)?.toString();
    this.expands.push(String(item) + (v ? `(${v})` : ""));
    // Extract the return type of the callback if provided
    type TBuilderReturn = ReturnType<NonNullable<typeof callback>>;
    type TCallbackShape = TBuilderReturn extends OdataBuilder<infer R, any> ? R : {};
    // Final return type logic
    type TCombined = TSelects extends never ? TCallbackShape : TCallbackShape & Pick<X, TSelects>;
    type TExtendedPropResType = NonNullable<TTT[Z]> extends Array<any> ? TCombined[] : TCombined;
    type TFull = R extends Array<any> ? ValuesType<R> : R;

    type BuilderReturnType =
      R extends Array<any>
        ? OdataBuilderLevel0<T, ({ [k in Z]: TExtendedPropResType } & TFull)[]>
        : OdataBuilderLevel0ForOne<T, { [k in Z]: TExtendedPropResType } & TFull>;

    return this as never as BuilderReturnType;
  };

  public orderBy = <Z extends ObjectPaths<T, 2>>(item: Z, dir: "asc" | "desc" = "asc") => {
    this.orderBys.push(`${item?.toString().replace(".", "/")} ${dir}`);
    return this;
  };

  public orderByCount = <Z extends ObjectPaths<T, 2>>(item: Z, dir: "asc" | "desc" = "asc") => {
    this.orderBys.push(`${item?.toString().replace(".", "/")}/$count ${dir}`);
    return this;
  };

  public top = (num: number) => {
    this.tops = num;
    return this;
  };
  public skip = (num: number) => {
    this.skips = num;
    return this;
  };
  public count = () => {
    this.counts = true;
    return this;
  };
  public param = (p: string, v: string | number) => {
    p && v && this.params.push(`${p}=${String(v)}`);
    return this;
  };
  public filter = <Z extends ObjectPaths<T, 2>>(
    item: Z,
    def: "eq" | "lt" | "gt" | "ge" | "le" | "ne" | "in",
    val: Z extends keyof T ? T[Z] : ParamValueType,
  ) => {
    this.filters.push(`${item?.toString().replace(/\./g, "/")} ${def} ${this.getParamValue(val as string)}`);
    return this;
  };
  public filterAny = <Z extends ObjectPaths<T, 2>>(item: Z, any: string = "") => {
    this.filters.push(`${item?.toString().replace(".", "/")}/any(${any})`);
    return this;
  };
  public filterAll = <Z extends ObjectPaths<T, 2>>(item: Z, all: string = "") => {
    this.filters.push(`${item?.toString().replace(".", "/")}/all(${all})`);
    return this;
  };
  public filterOr = <Z extends ObjectPaths<T, 2>>(
    ...fgs: [
      Z | ObjectPaths<T, 2>,
      "eq" | "lt" | "gt" | "ge" | "le" | "ne" | "in" | "contains" | "startsWith" | "endsWith",
      Z extends keyof T ? T[Z] : ParamValueType,
    ][]
  ) => {
    this.filters.push(
      `(${fgs
        .map((fg) =>
          !["contains", "startsWith", "endsWith"].includes(fg[1])
            ? `${fg[0]?.toString().replace(/\./g, "/")} ${fg[1]} ${this.getParamValue(fg[2] as string)}`
            : `${fg[1]}(toLower(${fg[0]?.toString().replace(/\./g, "/")}),${
                typeof fg[2] === "number" ? String(fg[2]) : `'${String(fg[2])?.toLocaleLowerCase()}'`
              })`,
        )
        .join(" or ")})`,
    );
    return this;
  };
  public filterIn = <Z extends ObjectPaths<T, 2>>(
    item: Z,
    val: (Z extends keyof T ? T[Z] : ParamValueType)[],
  ) => {
    val.length &&
      this.filters.push(`${item?.toString().replace(/\./g, "/")} in ${JSON.stringify(val as string[])}`);
    return this;
  };

  public filterNotIn = <Z extends ObjectPaths<T, 2>>(
    item: Z,
    val: (Z extends keyof T ? T[Z] : ParamValueType)[],
  ) => {
    val.length &&
      this.filters.push(
        `${item?.toString().replace(/\./g, "/")} in ${JSON.stringify(val as string[])} eq false`,
      );
    return this;
  };
  public filterSearch = <Z extends ObjectPaths<T, 2>>(
    columns: Z[],
    query: Z extends keyof T ? T[Z] : ParamValueType,
    func: "contains" | "startsWith" | "endsWith" = "contains",
    caseInsensitive: boolean = true,
  ) => {
    const sanitizedQuery = encodeURIComponent(String(query).replace(/'/g, "''"));
    sanitizedQuery !== "" &&
      this.filters.push(
        ...columns
          .map((column) => `${column}`.replace(/\./g, "/"))
          .map((column) => {
            const columnValue = caseInsensitive ? `tolower(${column})` : column;
            const queryValue = caseInsensitive ? `tolower('${sanitizedQuery}')` : `'${sanitizedQuery}'`;
            return `${func}(${columnValue}, ${queryValue})`;
          }),
      );
    return this;
  };
  public filterSearchOr = <Z extends ObjectPaths<T, 2>>(
    columns: Z[],
    queries: (Z extends keyof T ? T[Z] : ParamValueType)[],
    func: "contains" | "startsWith" | "endsWith" = "contains",
    caseInsensitive: boolean = true,
  ) => {
    if (!queries.length) return this;

    const orConditions = queries
      .map((query) => {
        const sanitizedQuery = encodeURIComponent(String(query).replace(/'/g, "''"));
        return sanitizedQuery
          ? `(${columns
              .map(
                (column) =>
                  `${func}(${caseInsensitive ? `tolower(${column})` : column}, ${caseInsensitive ? `tolower('${sanitizedQuery}')` : `'${sanitizedQuery}'`})`,
              )
              .join(" or ")})`
          : null;
      })
      .filter(Boolean);

    orConditions.length && this.filters.push(`(${orConditions.join(" or ")})`);

    return this;
  };
  public filterRaw = (filter: string) => {
    this.filters.push(encodeURIComponent(filter));
    return this;
  };
  public applyRaw = (cmd: "filter" | "groupby", a: string) => {
    this.applys.push(`${cmd}(${a})`);
    return this;
  };
  public applyFilter = <Z extends ObjectPaths<T, 2> | keyof T>(
    item: Z,
    def: "eq" | "lt" | "gt" | "ge" | "le" | "ne" | "in",
    val: Z extends keyof T ? T[Z] : ParamValueType,
  ) => {
    this.applys.push(
      `filter(${item?.toString().replace(/\./g, "/")} ${def} ${this.getParamValue(val as string)})`,
    );
    return this;
  };
  public applyFilterOr = <Z extends ObjectPaths<T, 2>>(
    ...fgs: [
      Z | ObjectPaths<T, 2>,
      "eq" | "lt" | "gt" | "ge" | "le" | "ne" | "in" | "contains" | "startsWith" | "endsWith",
      Z extends keyof T ? T[Z] : ParamValueType,
    ][]
  ) => {
    this.applys.push(
      `filter(${fgs
        .map((fg) =>
          !["contains", "startsWith", "endsWith"].includes(fg[1])
            ? `${fg[0]?.toString().replace(/\./g, "/")} ${fg[1]} ${this.getParamValue(fg[2] as string)}`
            : `${fg[1]}(toLower(${fg[0]?.toString().replace(/\./g, "/")}),${
                typeof fg[2] === "number" ? String(fg[2]) : `'${String(fg[2])?.toLocaleLowerCase()}'`
              })`,
        )
        .join(" or ")})`,
    );
    return this;
  };
  public applyFilterRaw = (val: string) => {
    this.applys.push(`filter(${val})`);
    return this;
  };
  public applyFilterIn = <Z extends ObjectPaths<T, 2> | keyof T>(
    item: Z,
    val: (Z extends keyof T ? T[Z] : ParamValueType)[],
    notIn?: boolean,
  ) => {
    val.length &&
      this.applys.push(
        `filter(${`${item?.toString().replace(/\./g, "/")} in ${JSON.stringify(val as string[])}`}${
          notIn ? " eq false" : ""
        })`,
      );
    return this;
  };
  public applyGroupBy = <
    Z extends ObjectPaths<T, 2>,
    X extends {
      by: ObjectPaths<T, 2>;
      with: "countdistinct" | "count" | "sum" | "avg" | "max" | "min";
      as: string;
    },
  >(
    items: Z[],
    ...aggregate: X[]
  ) => {
    let str = `(${items.map((item) => item?.toString().replace(/\./g, "/")).join(",")})`;
    if (aggregate?.length) {
      str += `,aggregate(${aggregate.map((ag) => `${(ag.by as string).replace(/\./g, "/")} with ${ag.with} as ${ag.as}`).join(",")})`;
    }
    this.applys.push(`groupby(${str})`);
    return this;
  };
  public toString = () => {
    let url = "";
    const addToUrl = (txt: string) => (url += `${this.level === 0 ? "&" : ";"}${txt}`);
    if (this.selects.length) {
      addToUrl(`select=${this.selects.join(",")}`);
    }
    if (this.expands.length) {
      addToUrl(`expand=${this.expands.join(",")}`);
    }
    if (this.orderBys.length) {
      addToUrl(`orderBy=${this.orderBys.join(",")}`);
    }
    if (this.tops !== null) {
      addToUrl(`top=${this.tops}`);
    }
    if (this.skips) {
      addToUrl(`skip=${this.skips}`);
    }
    if (this.counts) {
      addToUrl("count=true");
    }
    if (this.filters.length) {
      addToUrl(`filter=${this.filters.join(" and ")}`);
    }
    if (this.applys.length) {
      addToUrl(`apply=${this.applys.join("/")}`);
    }
    if (this.params.length) {
      addToUrl(`${this.params.join("&")}`);
    }
    return url?.length ? url.substring(1) : url;
  };
}

type Path = { [key in string]: string };

class OdataBuilderLevel0<
  T,
  R = T,
  TReqKey extends keyof ErrorPathTypes = keyof ErrorPathTypes,
> extends OdataBuilder<T, R> {
  public errorHandlers: { [key in string]: Function } = {};
  public catchClientError = <TTypeName extends ErrorPathTypes[TReqKey]["typename"]>(
    key: TTypeName,
    handler: (error: Extract<ErrorPathTypes[TReqKey], { typename: TTypeName }>) => void,
  ) => {
    this.errorHandlers[key] = handler;
    return this;
  };
  public catchAllApiErrors = (handler: (error: ApiError) => void) => {
    this.errorHandlers["*"] = handler;
    return this;
  };

  public $ = <AdditonalProps extends {}>() => {
    const query = this.toString();
    const url = this.embededParams
      ? this.setParams(this.path[this.op] || "", this.embededParams)
      : this.path[this.op];
    return getApiService()
      .apiCall$<{ value: object; "@odata.count"?: number }>(
        `${url}${query ? "?" : ""}${query || ""}`,
        this.method || "GET",
        null,
        this.body,
        Object.assign(this.options, { errorHandlers: this.errorHandlers }),
      )
      .pipe(
        map((res) => ({
          value: res?.value ?? res ?? null,
          count: res ? res["@odata.count"] : null,
        })),
      ) as Observable<{
      value: R extends Array<any> ? (ValuesType<R> & AdditonalProps)[] : R & AdditonalProps;
      count?: number;
    }>;
  };

  constructor(
    protected path: Path,
    protected op: keyof PathTypes,
    protected body?: object | null,
    protected method?: "GET" | "POST" | "PATCH" | "DELETE",
    protected options: ReqOptions = {},
    protected embededParams?: { [key in string]: ParamValueType },
  ) {
    super(0);
  }

  public debug = () => {
    return JSON.stringify(this);
  };
}

class OdataBuilderLevel0ForOne<
  T,
  R = T,
  TReqKey extends keyof ErrorPathTypes = keyof ErrorPathTypes,
> extends OdataBuilderLevel0<T, R, TReqKey> {
  private _mapper: ((r: any) => R) | null = null;
  public get mapper(): ((r: any) => R) | null {
    return this._mapper;
  }
  public set mapper(v: (r: any) => R) {
    this._mapper = v;
  }

  public $ = <AdditonalProps extends {}>() => {
    const query = this.toString();
    const url = this.embededParams
      ? this.setParams(this.path[this.op] || "", this.embededParams)
      : this.path[this.op];
    return getApiService()
      .apiCall$<{ value: object; "@odata.count"?: number }>(
        `${url}${query ? "?" : ""}${query || ""}`,
        this.method || "GET",
        null,
        this.body,
        Object.assign(this.options, { errorHandlers: this.errorHandlers }),
      )
      .pipe(
        map((res) => (this.mapper ? this.mapper(res?.value ?? res) : (res?.value ?? res ?? null))),
      ) as Observable<R & AdditonalProps>;
  };

  constructor(
    path: Path,
    op: keyof PathTypes,
    body?: object | null,
    method?: "GET" | "POST" | "PATCH" | "DELETE",
    options: ReqOptions = {},
    embededParams?: { [key in string]: ParamValueType },
  ) {
    super(path, op, body, method, options, embededParams);
  }
}
export const odataCount = <
  Res,
  K extends keyof ApiSchema.ApiGetCountPathTypes = keyof ApiSchema.ApiGetCountPathTypes,
  V = ApiSchema.ApiGetCountPathTypes[K],
>(
  op: K,
  options: ReqOptions = {},
) => {
  type T = Res & V;
  options.res = "text";
  const qb = new OdataBuilderLevel0<T>(ApiSchema.apiGetCountPaths, op, null, "GET", options);
  return qb;
};

export const odataFuncList = <
  K extends keyof ApiSchema.ApiFuncPathTypes = keyof ApiSchema.ApiFuncPathTypes,
  V = ApiSchema.ApiFuncPathTypes[K],
>(
  op: K,
  params?: ApiSchema.ApiFuncParams[K],
  options: ReqOptions = {},
) => {
  type T = V extends Array<any> ? ValuesType<V> : V;
  const qb = new OdataBuilderLevel0<T, T[]>(ApiSchema.apiFuncPaths, op, null, "GET", options, params);
  return qb;
};

export const odataFuncOne = <
  K extends keyof ApiSchema.ApiFuncPathTypes = keyof ApiSchema.ApiFuncPathTypes,
  V = ApiSchema.ApiFuncPathTypes[K],
>(
  op: K,
  params?: Nullable<ApiSchema.ApiFuncParams[K]>,
  options: ReqOptions = {},
) => {
  type T = V;
  const qb = new OdataBuilderLevel0ForOne<T, T>(
    ApiSchema.apiFuncPaths,
    op,
    null,
    "GET",
    options,
    params as never,
  );
  return qb;
};
export const odataAct = <
  K extends keyof ApiSchema.ApiActPathTypes = keyof ApiSchema.ApiActPathTypes,
  V = ApiSchema.ApiActPathTypes[K],
>(
  op: K,
  body: K extends keyof RequestBodyTypes ? RequestBodyTypes[K] : object,
  params?: ApiSchema.ApiActParams[K],
  options: ReqOptions = {},
) => {
  type T = V;
  const qb = new OdataBuilderLevel0ForOne<T, T>(ApiSchema.apiActPaths, op, body, "POST", options, params);
  return qb;
};

export const odataList = <
  Res,
  K extends keyof ApiSchema.ApiGetPathTypes = keyof ApiSchema.ApiGetPathTypes,
  V = ApiSchema.ApiGetPathTypes[K],
>(
  op: K,
  params?: ApiSchema.ApiGetParams[K],
  options: ReqOptions = {},
) => {
  type T = Res & V;
  const qb = new OdataBuilderLevel0<T, T[]>(ApiSchema.apiGetPaths, op, null, "GET", options, params);
  return qb;
};

export const odataFirst = <
  Res,
  K extends keyof ApiSchema.ApiGetPathTypes = keyof ApiSchema.ApiGetPathTypes,
  V = ApiSchema.ApiGetPathTypes[K],
>(
  op: K,
  params?: ApiSchema.ApiGetParams[K],
  options: ReqOptions & { orderBy?: string; orderByOrder?: "asc" | "desc" } = {},
) => {
  type T = Res & V;
  const qb = new OdataBuilderLevel0ForOne<T, T>(ApiSchema.apiGetPaths, op, null, "GET", options, params)
    .top(1)
    .orderBy((options?.orderBy || "id") as never, options?.orderByOrder ?? "asc");
  qb.mapper = (a) => {
    if (!a && (!Array.isArray(a) || a.length > 0)) {
      return null;
    }
    return Array.isArray(a) ? a[0] : a;
  };
  return qb;
};

export const odataGet = <
  Res,
  K extends keyof ApiSchema.ApiGetOnePathTypes = keyof ApiSchema.ApiGetOnePathTypes,
  V = ApiSchema.ApiGetOnePathTypes[K],
>(
  op: K,
  params: ApiSchema.ApiGetOneParams[K],
  options: ReqOptions = {},
) => {
  type T = Res & V;
  const qb = new OdataBuilderLevel0ForOne<T, T>(ApiSchema.apiGetOnePaths, op, null, "GET", options, params);
  return qb;
};

export const odataPost = <
  Res,
  K extends keyof ApiSchema.ApiPostPathTypes = keyof ApiSchema.ApiPostPathTypes,
  V = ApiSchema.ApiPostPathTypes[K],
>(
  op: K,
  body: K extends keyof RequestBodyTypes ? RequestBodyTypes[K] : object,
  params?: ApiSchema.ApiPostParams[K],
  options: ReqOptions = {},
) => {
  type T = Res & V;
  const qb = new OdataBuilderLevel0ForOne<T>(
    ApiSchema.apiPostPaths,
    op,
    body,
    "POST",
    options,
    params as never,
  );
  return qb;
};

export const odataPostOne = <
  Res,
  K extends keyof ApiSchema.ApiPostPathTypes = keyof ApiSchema.ApiPostPathTypes,
  V = ApiSchema.ApiPostPathTypes[K],
>(
  op: K,
  body: K extends keyof RequestBodyTypes ? RequestBodyTypes[K] : object,
  params?: ApiSchema.ApiPostParams[K],
  options: ReqOptions = {},
) => {
  type T = Res & V;
  const qb = new OdataBuilderLevel0ForOne<T>(
    ApiSchema.apiPostPaths,
    op,
    body,
    "POST",
    options,
    params as never,
  );
  return qb;
};
export const odataPatchOne = <
  Res,
  K extends keyof ApiSchema.ApiPatchOnePathTypes = keyof ApiSchema.ApiPatchOnePathTypes,
  V = ApiSchema.ApiPatchOnePathTypes[K],
>(
  op: K,
  body: K extends keyof RequestBodyTypes ? RequestBodyTypes[K] : object,
  params: ApiSchema.ApiPatchOneParams[K],
  options: ReqOptions = {},
) => {
  type T = Res & V;
  const qb = new OdataBuilderLevel0ForOne<T>(ApiSchema.apiPatchOnePaths, op, body, "PATCH", options, params);
  return qb;
};

export const odataDeleteOne = <
  Res,
  K extends keyof ApiSchema.ApiDeleteOnePathTypes = keyof ApiSchema.ApiDeleteOnePathTypes,
  V = ApiSchema.ApiDeleteOnePathTypes[K],
>(
  op: K,
  params: ApiSchema.ApiDeleteOneParams[K],
  options: ReqOptions = {},
) => {
  type T = Res & V;
  const qb = new OdataBuilderLevel0ForOne<T>(
    ApiSchema.apiDeleteOnePaths,
    op,
    null,
    "DELETE",
    options,
    params,
  );
  return qb;
};

export class OdataMockStub2<T, R> extends OdataBuilder<T, R> {
  public returnValue: {
    count: number;
    value: (R extends Array<any> ? ValuesType<R>[] : R) | null;
  } | null = null;
  public setTestReturnValue = (val: {
    count: number;
    value: (R extends Array<any> ? ValuesType<R>[] : R) | null;
  }) => {
    this.returnValue = val;
  };
  public $ = <AdditonalProps extends {}>() => {
    return of(this.returnValue) as Observable<{
      value: R extends Array<any> ? (ValuesType<R> & AdditonalProps)[] : R & AdditonalProps;
      count?: number;
    }>;
  };
  constructor() {
    super(0);
  }
}
